import { memo, useCallback, useState } from 'react';
import type { NodeProps, Node } from '@xyflow/react';
import { useEntityById } from '@/lib/sync/use-entity-by-id';
import type { EntityProxyData } from '@/features/canvas/engine/schema/types';
import { EntityProxyNodeView } from '@/features/canvas/engine/nodes/entity-proxy-node-view';
import { notification } from '@nula/core-ui';
import { promoteVirtualNode, LockContentionError } from './api/promote-virtual-node';
import { useCanvasContext } from './canvas-context';

type EntityProxyRFNode = Node<EntityProxyData, 'entity-proxy'>;

/**
 * Shell wrapper for the entity-proxy node type. Reads the live entity row
 * from `entityStore` via `useEntityById` and passes it down to the pure
 * engine view `EntityProxyNodeView`.
 *
 * Engine/shell seam: this file MAY import from @sync, @adapters, @services,
 * @routes, @session, and @stores. The engine view (EntityProxyNodeView) MUST NOT.
 *
 * Promote handler: wired to the real `POST /api/canvas/:id/promote-virtual`
 * endpoint (S11). On success the server writes back to Yjs with origin
 * 'server:promotion', flipping isVirtual=false and setting entityId via WS sync.
 * On LockContentionError: one retry after 2s, then notification.warning.
 * On other errors: notification.error.
 */
export const ConnectedEntityProxyNode = memo(function ConnectedEntityProxyNode(
  props: NodeProps<EntityProxyRFNode>,
) {
  const { id, data, selected, parentId } = props;
  const resolved = useEntityById(data.entityId, data.entityKind);
  const { canvasId } = useCanvasContext();
  const [promoting, setPromoting] = useState(false);

  // The engine→shell callback is single-arg (nodeId); canvasId is captured
  // from the shell's canvas context so the engine seam stays clean.
  const handlePromote = useCallback(async (nodeId: string) => {
    setPromoting(true);
    try {
      await promoteVirtualNode(canvasId, nodeId);
      // Server writes back via 'server:promotion' origin — Yjs sync flips state.
      // No extra client write needed.
    } catch (err) {
      if (err instanceof LockContentionError) {
        // One retry after 2s
        await new Promise((r) => setTimeout(r, 2000));
        try {
          await promoteVirtualNode(canvasId, nodeId);
        } catch (err2) {
          if (err2 instanceof LockContentionError) {
            notification.warning({
              message:
                'Another promotion is in progress, please retry shortly.',
            });
          } else {
            notification.error({
              message:
                err2 instanceof Error ? err2.message : 'Promotion failed',
            });
          }
        }
      } else {
        notification.error({
          message: err instanceof Error ? err.message : 'Promotion failed',
        });
      }
    } finally {
      setPromoting(false);
    }
  }, [canvasId]);

  return (
    <EntityProxyNodeView
      nodeId={id}
      data={data}
      resolved={resolved}
      selected={selected}
      parentId={parentId ?? null}
      promoting={promoting}
      onTriggerVirtualPromotion={data.isVirtual ? handlePromote : undefined}
    />
  );
});
